import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { KeyRound, ShieldCheck, LogOut } from "lucide-react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { StrengthMeter } from "@/pages/PasswordReset";

export default function AccountSecurity() {
  const { user, logout } = useApp();
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  const rulesOk = /[A-Za-z]/.test(form.next) && /\d/.test(form.next) && /[^A-Za-z0-9]/.test(form.next) && form.next.length >= 10;
  const canSubmit = form.current && rulesOk && form.next === form.confirm;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return toast.error("Please meet every password requirement and confirm.");
    if (form.current === form.next) return toast.error("New password must be different from current.");
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current, new_password: form.next,
      });
      toast.success("Password updated — please sign in again with your new password.");
      setForm({ current: "", next: "", confirm: "" });
      setTimeout(() => { logout(); window.location.href = "/login"; }, 1200);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="account-security">
      <div className="mb-6">
        <div className="text-xs font-mono uppercase tracking-widest text-teal-600">Account · Security</div>
        <h1 className="font-display font-extrabold text-3xl text-slate-900 mt-1">Change your password</h1>
        <p className="text-slate-600 mt-2">Signed in as <b>{user?.email}</b> ({user?.role}). All active sessions will be signed out after a successful change.</p>
      </div>

      <Card>
        <CardContent className="p-6 space-y-4">
          <h2 className="font-semibold text-slate-900 flex items-center gap-2"><KeyRound className="w-5 h-5 text-teal-600" />Rotate password</h2>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>Current password *</Label>
              <Input type="password" required value={form.current} onChange={e => setForm({ ...form, current: e.target.value })} data-testid="cp-current" className="mt-1.5" autoComplete="current-password" />
            </div>
            <div>
              <Label>New password *</Label>
              <Input type="password" required value={form.next} onChange={e => setForm({ ...form, next: e.target.value })} data-testid="cp-next" className="mt-1.5" autoComplete="new-password" />
              <StrengthMeter password={form.next} />
            </div>
            <div>
              <Label>Confirm new password *</Label>
              <Input type="password" required value={form.confirm} onChange={e => setForm({ ...form, confirm: e.target.value })} data-testid="cp-confirm" className="mt-1.5" autoComplete="new-password" />
              {form.confirm && form.confirm !== form.next && (
                <div className="text-xs text-red-600 mt-1">Passwords do not match</div>
              )}
            </div>
            <div className="flex items-start gap-2 text-xs text-slate-500 bg-teal-50 p-3 rounded-lg">
              <ShieldCheck className="w-4 h-4 text-teal-600 flex-shrink-0 mt-0.5" />
              <span>Passwords are salted &amp; hashed with bcrypt. CLEANBAZ never stores or sees your password. After rotation, previous reset links become invalid.</span>
            </div>
            <Button type="submit" disabled={busy || !canSubmit} data-testid="cp-submit" className="bg-teal-600 hover:bg-teal-700 text-white disabled:bg-slate-300 disabled:cursor-not-allowed">
              <LogOut className="w-4 h-4 mr-1" />{busy ? "Updating…" : "Update password &amp; sign out"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
